import { adkitApi } from "../adkitApi";
import { stripUndefinedParams } from "../queryParams";
import {
  ClubhouseAdKitDtoSchema,
  ClubhouseAdKitDtoArraySchema,
  ClubhouseAdKitResponseSchema,
  ClubhouseAdKitSyncResponseSchema,
  ClubhouseAdKitActivationResponseSchema,
} from "entities/responses/adkit";
import type {
  ClubhouseAdKitDto,
  ClubhouseAdKitResponse,
  ClubhouseAdKitSyncResponse,
  ClubhouseAdKitActivationResponse,
} from "entities/responses/adkit";
import {
  CreateAdKitDtoSchema,
  UpdateAdKitDtoSchema,
} from "entities/requests/adkit";
import type { CreateAdKitDto, UpdateAdKitDto } from "entities/requests/adkit";
import type { SluggedId } from "Models";

const urls = {
  facilityAdKits(facilityId: string): string {
    return `/api/ad-kit/facility/${facilityId}/adkits`;
  },
  adKit(facilityId: string, adKitId: SluggedId): string {
    return `/api/ad-kit/facility/${facilityId}/adkit/${adKitId}`;
  },
  sync(facilityId: string, adKitId: SluggedId): string {
    return `${urls.adKit(facilityId, adKitId)}/sync`;
  },
  activate(facilityId: string, adKitId: SluggedId): string {
    return `${urls.adKit(facilityId, adKitId)}/activate`;
  },
  deactivate(facilityId: string, adKitId: SluggedId): string {
    return `${urls.adKit(facilityId, adKitId)}/deactivate`;
  },
};

type FacilityArgs = { facilityId: string };
type GetFacilityAdKitsArgs = FacilityArgs & { includeDeleted?: boolean };
type AdKitArgs = FacilityArgs & { adKitId: SluggedId };
type CreateAdKitArgs = FacilityArgs & { body: CreateAdKitDto };
type UpdateAdKitArgs = AdKitArgs & { body: UpdateAdKitDto };
type SyncAdKitArgs = AdKitArgs & {
  simulatorIds: string[];
  imageOverwrite?: boolean;
};
type ActivateAdKitArgs = AdKitArgs & {
  simulatorIds: string[];
  syncFirst?: boolean;
};

const adkitEndpoints = adkitApi.injectEndpoints({
  endpoints: (build) => ({
    // Always fetched with includeDeleted so the dashboard can bucket Live,
    // Not live and Archived from one call rather than three.
    getFacilityAdKits: build.query<ClubhouseAdKitDto[], GetFacilityAdKitsArgs>({
      query: ({ facilityId, includeDeleted }) => ({
        url: urls.facilityAdKits(facilityId),
        method: "GET",
        params: stripUndefinedParams({ includeDeleted }),
      }),
      transformResponse: (raw: unknown) =>
        ClubhouseAdKitDtoArraySchema.parse(raw),
      providesTags: (result) =>
        result
          ? [
              ...result.map((a) => ({ type: "AdKit" as const, id: a.id })),
              "AdKitList",
            ]
          : ["AdKitList"],
    }),

    // Always fetched with includeDeleted — the detail page shows an archived
    // campaign (badge, Restore button) rather than treating it as not found,
    // and without this the GET 404s right after an archive's own refetch.
    getAdKit: build.query<ClubhouseAdKitDto, AdKitArgs>({
      query: ({ facilityId, adKitId }) => ({
        url: urls.adKit(facilityId, adKitId),
        method: "GET",
        params: { includeDeleted: true },
      }),
      transformResponse: (raw: unknown) => ClubhouseAdKitDtoSchema.parse(raw),
      providesTags: (_result, _error, { adKitId }) => [
        { type: "AdKit", id: adKitId },
      ],
    }),

    // Returns the whole envelope: a 201 can still carry errors for the
    // locations or instances the API skipped, and setup has to say so.
    createAdKit: build.mutation<ClubhouseAdKitResponse, CreateAdKitArgs>({
      query: ({ facilityId, body }) => ({
        url: urls.facilityAdKits(facilityId),
        method: "POST",
        body: CreateAdKitDtoSchema.parse(body),
      }),
      // The real response is an envelope ({result, errors}, per
      // Clubhouse_AdKitResponse) — unlike GET, which returns the bare DTO.
      // Parsing the envelope as a bare DTO throws, and a transformResponse
      // throw rejects the mutation WITHOUT rejectWithValue, which RTK
      // Query's invalidation middleware only acts on for a value-carrying
      // rejection — so invalidatesTags silently never ran, and this save
      // never refreshed the UI despite the PATCH/POST succeeding.
      transformResponse: (raw: unknown) =>
        ClubhouseAdKitResponseSchema.parse(raw),
      invalidatesTags: ["AdKitList"],
    }),

    updateAdKit: build.mutation<ClubhouseAdKitDto, UpdateAdKitArgs>({
      query: ({ facilityId, adKitId, body }) => ({
        url: urls.adKit(facilityId, adKitId),
        method: "PATCH",
        body: UpdateAdKitDtoSchema.parse(body),
      }),
      transformResponse: (raw: unknown) =>
        ClubhouseAdKitResponseSchema.parse(raw).result,
      invalidatesTags: (_result, _error, { adKitId }) => [
        { type: "AdKit", id: adKitId },
        "AdKitList",
      ],
    }),

    deleteAdKit: build.mutation<void, AdKitArgs>({
      query: ({ facilityId, adKitId }) => ({
        url: urls.adKit(facilityId, adKitId),
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { adKitId }) => [
        { type: "AdKit", id: adKitId },
        "AdKitList",
      ],
    }),

    syncAdKit: build.mutation<ClubhouseAdKitSyncResponse, SyncAdKitArgs>({
      query: ({ facilityId, adKitId, simulatorIds, imageOverwrite }) => ({
        url: urls.sync(facilityId, adKitId),
        method: "POST",
        body: { simulatorIds, imageOverwrite: imageOverwrite ?? true },
      }),
      transformResponse: (raw: unknown) =>
        ClubhouseAdKitSyncResponseSchema.parse(raw),
      invalidatesTags: (_result, _error, { adKitId }) => [
        { type: "AdKit", id: adKitId },
      ],
    }),

    // Carries x-correlation-id (success and failure) so the Go live drawer's
    // "Technical details" can give aG Support something to search for.
    activateAdKit: build.mutation<
      ClubhouseAdKitActivationResponse & { correlationId?: string },
      ActivateAdKitArgs
    >({
      query: ({ facilityId, adKitId, simulatorIds, syncFirst }) => ({
        url: urls.activate(facilityId, adKitId),
        method: "POST",
        body: { simulatorIds, syncFirst },
      }),
      transformResponse: (raw: unknown, meta) => ({
        ...ClubhouseAdKitActivationResponseSchema.parse(raw),
        correlationId:
          meta?.response?.headers.get("x-correlation-id") ?? undefined,
      }),
      transformErrorResponse: (error, meta) => ({
        ...error,
        correlationId:
          meta?.response?.headers.get("x-correlation-id") ?? undefined,
      }),
      invalidatesTags: (_result, _error, { adKitId }) => [
        { type: "AdKit", id: adKitId },
        "AdKitList",
      ],
    }),

    deactivateAdKit: build.mutation<
      ClubhouseAdKitActivationResponse,
      ActivateAdKitArgs
    >({
      query: ({ facilityId, adKitId, simulatorIds, syncFirst }) => ({
        url: urls.deactivate(facilityId, adKitId),
        method: "POST",
        body: { simulatorIds, syncFirst },
      }),
      transformResponse: (raw: unknown) =>
        ClubhouseAdKitActivationResponseSchema.parse(raw),
      invalidatesTags: (_result, _error, { adKitId }) => [
        { type: "AdKit", id: adKitId },
        "AdKitList",
      ],
    }),
  }),
});

export const {
  useGetFacilityAdKitsQuery,
  useGetAdKitQuery,
  useCreateAdKitMutation,
  useUpdateAdKitMutation,
  useDeleteAdKitMutation,
  useSyncAdKitMutation,
  useActivateAdKitMutation,
  useDeactivateAdKitMutation,
} = adkitEndpoints;
